import { CustomProperties, init, track } from "@plausible-analytics/tracker";
import type { FC, ReactNode } from "react";
import { createContext, useCallback, useEffect, useMemo } from "react";

import { AnalyticsEvent } from "./types";

type AnalyticsContextValue = {
  track: (eventName: AnalyticsEvent, props?: CustomProperties) => void;
};

type Props = {
  children: ReactNode;
};

export const AnalyticsContext = createContext<AnalyticsContextValue>({
  track: () => undefined,
});

let hasInitialized = false;

export const AnalyticsProvider: FC<Props> = ({ children }) => {
  useEffect(() => {
    if (hasInitialized) {
      return;
    }

    init({
      domain: process.env.PLAUSIBLE_DOMAIN || "",
      endpoint: `${process.env.PLAUSIBLE_API_HOST}/api/event`,
      autoCapturePageviews: true,
      captureOnLocalhost: false,
      outboundLinks: true,
    });

    hasInitialized = true;
  }, []);

  const trackEvent = useCallback(
    (eventName: AnalyticsEvent, props: CustomProperties = {}) => {
      if (!hasInitialized) {
        return;
      }

      track(eventName, { props });
    },
    [],
  );

  const contextValue = useMemo<AnalyticsContextValue>(
    () => ({
      track: trackEvent,
    }),
    [trackEvent],
  );

  return (
    <AnalyticsContext.Provider value={contextValue}>
      {children}
    </AnalyticsContext.Provider>
  );
};
